import { Injectable, inject, DestroyRef, signal } from '@angular/core';
import { Subject, fromEvent, interval, merge, Subscription } from 'rxjs';
import { debounceTime, tap, takeWhile } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Service
import { AuthService } from '../auth.service';
import { LoggerService } from '../log-level.service';

@Injectable({
  providedIn: 'root',
})
export class UserInactivityService {
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(LoggerService);

  //private readonly inactivityTime = 2 * 60 * 1000; // 2 minutos en milisegundos
  private readonly inactivityTime = 1000 * 60 * 1000; // 2 minutos en milisegundos
  private readonly logoutWarningTime = 30 * 1000; // 30 segundos de advertencia antes de logout
  private userActivity = new Subject<void>();
  private isLoggedIn = true;
  private isMonitoringActive = false;
  private inactivitySubscription: Subscription | null = null;
  private countdownInterval: any = null;

  // Señales para controlar el modal de advertencia
  showWarningModal = signal(false);
  remainingSeconds = signal(30);

  private activityEvents = [
    'click',
    'mousedown',
    'mousemove',
    'keydown',
    'scroll',
    'touchstart',
  ];

  /**
   * Iniciar el monitoreo de inactividad
   */
  startWatching(): void {
    // Evitar inicialización múltiple
    if (this.isMonitoringActive) {
      this.logger.debug('El monitoreo ya está activo, ignorando llamada');
      return;
    }

    this.logger.debug('Iniciando monitoreo de inactividad');
    this.isMonitoringActive = true;
    this.isLoggedIn = true;

    // Combinar todos los eventos de actividad en un solo observable
    const activityObservable = merge(
      ...this.activityEvents.map((event) => fromEvent(document, event))
    ).pipe(
      debounceTime(300), // Agrupar eventos múltiples
      tap(() => this.userActivity.next()),
      takeUntilDestroyed(this.destroyRef)
    );

    // Suscribirse a eventos de actividad del usuario
    activityObservable.subscribe();

    // Iniciar el temporizador de inactividad
    this.checkUserInactivity();
  }

  /**
   * Verificar inactividad del usuario
   */
  private checkUserInactivity(): void {
    // Cancelar cualquier temporizador anterior
    if (this.inactivitySubscription) {
      this.inactivitySubscription.unsubscribe();
      this.inactivitySubscription = null;
    }

    let inactivityTimer = this.inactivityTime;
    let warningShown = false;

    // Limpiar el intervalo de cuenta regresiva anterior si existe
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }

    // Asegurarse de que el modal esté oculto al iniciar
    this.hideLogoutWarning();

    // Crear una suscripción para manejar la actividad del usuario
    const activitySubscription = this.userActivity.subscribe(() => {
      // Reiniciar el temporizador cuando hay actividad
      inactivityTimer = this.inactivityTime;

      // Si la advertencia estaba visible, ocultarla
      if (warningShown) {
        this.hideLogoutWarning();
        warningShown = false;

        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
          this.countdownInterval = null;
        }
      }
    });

    // Crear el temporizador principal de inactividad
    this.inactivitySubscription = interval(1000) // Verificar cada segundo
      .pipe(
        takeWhile(() => this.isLoggedIn && this.isMonitoringActive),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        inactivityTimer -= 1000;

        // Mostrar tiempo restante en consola durante todo el período de inactividad
        // const totalSecondsRemaining = Math.ceil(inactivityTimer / 1000);
        // const minutes = Math.floor(totalSecondsRemaining / 60);
        // const seconds = totalSecondsRemaining % 60;
        // this.logger.debug(
        //   `Tiempo restante para cierre de sesión: ${minutes}:${
        //     seconds < 10 ? '0' + seconds : seconds
        //   }`
        // );

        // Mostrar advertencia cuando falten 30 segundos
        if (inactivityTimer <= this.logoutWarningTime && !warningShown) {
          this.logger.warn('¡ADVERTENCIA! Cierre de sesión inminente.');
          this.showLogoutWarning();
          warningShown = true;

          // Iniciar el contador de segundos para mostrar en el modal
          let secondsRemaining = Math.ceil(inactivityTimer / 1000);
          this.remainingSeconds.set(secondsRemaining);

          this.countdownInterval = setInterval(() => {
            secondsRemaining--;
            this.remainingSeconds.set(secondsRemaining);

            // Mostrar la cuenta regresiva final en la consola
            this.logger.debug(
              `CUENTA REGRESIVA: ${secondsRemaining} segundos para cierre de sesión automático`
            );

            if (secondsRemaining <= 0) {
              clearInterval(this.countdownInterval);
              this.countdownInterval = null;
            }
          }, 1000);
        }

        // Desconectar cuando el tiempo se agote
        if (inactivityTimer <= 0) {
          this.logger.warn('¡TIEMPO AGOTADO! Cerrando sesión...');
          this.isLoggedIn = false;
          this.hideLogoutWarning();

          if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
          }

          this.logout();
        }
      });

    // Asegurarse de limpiar la suscripción de actividad cuando se detenga el monitoreo
    this.destroyRef.onDestroy(() => {
      activitySubscription.unsubscribe();
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }
    });
  }

  /**
   * Mostrar advertencia de cierre de sesión inminente
   */
  private showLogoutWarning(): void {
    this.showWarningModal.set(true);
  }

  /**
   * Ocultar advertencia de cierre de sesión
   */
  private hideLogoutWarning(): void {
    this.showWarningModal.set(false);
  }

  /**
   * Continuar la sesión (reinicia el contador)
   */
  continueSession(): void {
    this.logger.debug(
      'Continuando sesión - Usuario hizo clic en el botón Continuar'
    );

    // Asegurarse de que el modal se cierre inmediatamente
    this.hideLogoutWarning();

    // Detener cualquier cuenta regresiva en curso
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }

    // Reiniciar el temporizador por completo
    this.userActivity.next();

    // Para el caso de problemas persistentes, forzar un reinicio del monitoreo
    this.stopWatching();
    setTimeout(() => {
      if (this.isMonitoringActive === false) {
        this.startWatching();
      }
    }, 100);
  }

  /**
   * Cerrar sesión del usuario
   */
  private logout(): void {
    this.logger.warn('Cerrando sesión por inactividad');
    this.hideLogoutWarning();
    this.stopWatching();

    //this.authService.logout();
    this.authService.logoutStorage();

    // Usar redirección directa en lugar de router.navigate
    window.location.href = '/';
  }

  /**
   * Detener el monitoreo de inactividad
   */
  stopWatching(): void {
    this.logger.debug('Deteniendo monitoreo de inactividad');
    this.isMonitoringActive = false;
    this.isLoggedIn = false;

    // Ocultar el modal si está visible
    this.hideLogoutWarning();

    // Limpiar intervalos y suscripciones
    if (this.inactivitySubscription) {
      this.inactivitySubscription.unsubscribe();
      this.inactivitySubscription = null;
    }

    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }
}
